import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import styles from "./GarudaIntroductionText.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBinoculars,
  faBrain,
  faChartDiagram,
  faCircleNodes,
  faCloud,
  faCubes,
  faGears,
  faLink,
  faMicroscope,
  faObjectUngroup,
  faRobot,
  faShield,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { faCodepen } from "@fortawesome/free-brands-svg-icons";

export interface IntroCardProps {
  title: string;
  icon: IconDefinition;
  textContent: string;
}

export const IntroCard = ({ title, icon, textContent }: IntroCardProps) => {
  const theme = useTheme();

  return (
    <Grid item xs={12} md={4}>
      <Card className={styles.card}>
        <CardHeader
          avatar={
            <FontAwesomeIcon
              icon={icon}
              size="2x"
              color={theme.palette.primary.main}
            />
          }
          title={
            <Typography
              gutterBottom
              variant="h5"
              component="h2"
              sx={{ paddingTop: "2%" }}
            >
              {title}
            </Typography>
          }
        />
        <CardContent>
          <Typography
            variant="body2"
            component="p"
            sx={{ color: "#939191" }}
          >
            {textContent}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );
};

export const CoreFeatures = () => {
  return (
    <>
      <Typography variant="h1" sx={{ marginBottom: "2%" }}>
        Garuda2x: Discover, Connect, Automate
      </Typography>
      <Typography
        variant="body1"
        sx={{ marginBottom: "1rem" }}
      >
        Garuda2x is the next-generation platform designed for the pharmaceutical
        and biomedical industries, redefining how diverse software tools and
        workflows integrate and operate. Built on the principles of
        discoverability and connectivity, Garuda2x enables seamless interaction
        across systems, locations and data silos while providing powerful
        automation capabilities tailored to modern life sciences.
      </Typography>
      <Grid container spacing={6}>
        <IntroCard
          title="Discoverability and Connectivity"
          icon={faCircleNodes}
          textContent="At the heart of Garuda2x is its ability to connect varied software components or Agents across machines and locations. These Agents perform specific functions and can be discovered and combined dynamically to address evolving research and operational needs. Garuda2x ensures that every tool in your ecosystem is not only accessible but also interoperable."
        />
        <IntroCard
          title="Cloud-First Architecture"
          icon={faCloud}
          textContent="Whether deployed locally, in the cloud or in hybrid environments, Garuda2x delivers unmatched flexibility. Its centralized Garuda Core allows users to connect tools and data seamlessly, regardless of their physical or virtual location."
        />

        <IntroCard
          title="Workflows made easy with Recipes"
          icon={faChartDiagram}
          textContent="The Recipe Builder lets you construct end-to-end workflows by piecing together Agents into executable sequences. These Recipes simplify complex processes, enabling automation, repeatability, and scalability for even the most intricate operations. Once built, workflows can be executed effortlessly via the Recipe Runner."
        />
        <IntroCard
          title="Enhanced Security"
          icon={faShield}
          textContent="Data security and compliance are integral to Garuda2x. With robust safeguards, the platform ensures secure operations without compromising performance or usability."
        />
        <IntroCard
          title="Custom Solutions"
          icon={faGears}
          textContent="A dedicated technology team is available to transform your software scripts into production-grade Agents, fully integrated into the Garuda2x ecosystem. Whether it's customization or scaling, we provide the expertise you need to succeed."
        />
      </Grid>
    </>
  );
};

export interface GarudaUspProps {
  title: string;
  textContent: string;
}

export const GarudaUsp = ({ title, textContent }: GarudaUspProps) => {
  return (
    <>
      <Typography variant="h2" sx={{ mt: 4, mb: 3 }}>
        {title}
      </Typography>
      <Typography variant="body1">{textContent}</Typography>
    </>
  );
};

export interface GarudaListDisplayItemProps {
  icon: IconDefinition;
  title: string;
  textContent: string;
}

export const GarudaListDisplayItem = ({
  icon,
  title,
  textContent,
}: GarudaListDisplayItemProps) => {
  const theme = useTheme();

  return (
    <ListItem>
      <ListItemIcon>
        <FontAwesomeIcon
          icon={icon}
          size="2x"
          color={theme.palette.primary.main}
        />
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {title}
          </Typography>
        }
        secondary={<Typography variant="body2" sx={{ color: "#939191" }}>{textContent}</Typography>}
      />
    </ListItem>
  );
};

export interface GarudaListDisplayProps {
  model: ListModel;
}

interface ListModel {
  title: string;
  items: ListModelItem[];
}

interface ListModelItem {
  icon: IconDefinition;
  title: string;
  textContent: string;
}

const howGarudaWorks: ListModel = {
  title: "How Garuda2x Works ?",
  items: [
    {
      title: "Agents",
      icon: faRobot,
      textContent:
        "Specialized software components designed to perform specific tasks. Agents are modular and interoperable, forming the building blocks of your workflows.",
    },
    {
      title: "Garuda Core",
      icon: faBrain,
      textContent:
        "The central engine enabling communication, discoverability, and connectivity across Agents.",
    },
    {
      title: "Recipe Builder",
      icon: faCubes,
      textContent:
        "A user-friendly editor for creating custom workflows by linking Agents together.",
    },
    {
      title: "Recipe Runner",
      icon: faGears,
      textContent:
        "An execution hub that brings your workflows to life, automating repetitive processes and ensuring consistency.",
    },
  ],
};

export const GarudaListDisplay = ({ model }: GarudaListDisplayProps) => {
  return (
    <Paper elevation={1} className={styles.garudaCard}>
      <Typography variant="h4">
        {model.title}
      </Typography>
      <List>
        {model.items.map((item, index) => (
          <GarudaListDisplayItem
            key={index}
            icon={item.icon}
            title={item.title}
            textContent={item.textContent}
          />
        ))}
      </List>
    </Paper>
  );
};

export const PoweringTheFuture = () => {
  const theme = useTheme();
  return (
    <Box sx={{ mt: 5 }}>
      <Typography variant="h4">
        <FontAwesomeIcon
          icon={faMicroscope}
          color={theme.palette.primary.main}
        />{" "}
        Powering the Future of Biomedical Innovation
      </Typography>
      <Typography variant="body1" mt={3}>
        Garuda2x bridges the gap between tools, systems, and insights. It is
        designed for organizations seeking to maximize the value of their
        software ecosystem while minimizing operational bottlenecks. From drug
        discovery to clinical data analysis, Garuda2x is the foundation for
        scalable, secure, and collaborative innovation.
      </Typography>
    </Box>
  );
};

const garudaUsp: ListModel = {
  title: "What sets Garuda2x apart ?",
  items: [
    {
      title: "Interoperability Without Boundaries",
      icon: faLink,
      textContent:
        "Agents enable integration of tools, databases, and analytics services across distributed systems, bridging gaps between varied software environments.",
    },
    {
      title: "Dynamic Discoverability",
      icon: faBinoculars,
      textContent:
        "Unlike rigid platforms, Garuda2x ensures that new tools and Agents can be seamlessly added and discovered in real time, making your ecosystem ever-evolving.",
    },
    {
      title: "Automation at Scale",
      icon: faCodepen,
      textContent:
        "Recipes empower users to design, test, and execute automated workflows tailored to specific biomedical and pharmaceutical challenges.",
    },
    {
      title: "Future-Proof Architecture",
      icon: faObjectUngroup,
      textContent:
        "With its cloud-first design, Garuda2x adapts to your infrastructure and scales effortlessly as your needs grow.",
    },
  ],
};
export const GarudaIntroductionText = () => {
  return (
    <div className={styles.container}>
      <CoreFeatures />
      <Grid container spacing={10} sx={{ marginTop: "1%" }}>
        <Grid item xs={12} md={6}>
          <GarudaListDisplay model={howGarudaWorks} />
        </Grid>
        <Grid item xs={12} md={6}>
          <GarudaListDisplay model={garudaUsp} />
        </Grid>
      </Grid>
      <PoweringTheFuture />
    </div>
  );
};
